import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { firstValueFrom } from 'rxjs';
import { environment } from '@environments/environment';

const TOKEN_KEY = 'fcm_token';
/** User turned phone notifications off in Delivery — don't re-register on start. */
const OPT_OUT_KEY = 'fcm_opt_out';
/** The one-time permission prompt after login was already shown. */
const ASKED_KEY = 'fcm_asked';

function lsGet(key: string): string | null {
	try {
		return localStorage.getItem(key);
	} catch {
		return null;
	}
}

function lsSet(key: string, value: string | null): void {
	try {
		if (value == null) {
			localStorage.removeItem(key);
		} else {
			localStorage.setItem(key, value);
		}
	} catch {
		/* storage blocked — worst case we ask again */
	}
}

/**
 * NativePushService — phone notifications in the Android app via Firebase Cloud Messaging.
 *
 * The backend Notifier sends one FCM message per registered device token
 * (`POST /notifications/devices`). Here we: ask for the OS permission, obtain the
 * token, register it for the logged-in user, drop it on logout (so the next account
 * on this phone doesn't get the previous one's alerts) and open `data.link` when a
 * notification is tapped. Web builds never touch this — `WebPushService` delegates
 * here only on `Capacitor.isNativePlatform()`.
 */
@Injectable({ providedIn: 'root' })
export class NativePushService {
	private readonly _http = inject(HttpClient);
	private readonly _router = inject(Router);
	private readonly _zone = inject(NgZone);

	readonly subscribed = signal<boolean>(false);
	readonly busy = signal<boolean>(false);

	private _listening = false;
	private _pendingToken: ((token: string | null) => void) | null = null;

	/** Listeners — once per app start (NativeService.init). */
	async init(): Promise<void> {
		if (this._listening) {
			return;
		}

		this._listening = true;

		await PushNotifications.addListener('registration', ({ value }) => {
			const resolve = this._pendingToken;
			this._pendingToken = null;

			if (resolve) {
				resolve(value);
			} else if (this.subscribed()) {
				// FCM rotated the token while we're opted in → keep the backend in sync.
				void this.sendToken(value);
			}
		});
		await PushNotifications.addListener('registrationError', (err) => {
			console.warn('[FCM] registration failed', err);
			this._pendingToken?.(null);
			this._pendingToken = null;
		});
		await PushNotifications.addListener(
			'pushNotificationActionPerformed',
			({ notification }) => {
				const link = (notification.data as Record<string, string> | undefined)?.link;

				if (link && link.startsWith('/')) {
					this._zone.run((): void => void this._router.navigateByUrl(link));
				}
			},
		);
	}

	/**
	 * After login / on start with a session: silently re-register when already allowed,
	 * ask once when the OS hasn't been asked yet, do nothing when the user opted out.
	 */
	async onSession(): Promise<void> {
		if (lsGet(OPT_OUT_KEY)) {
			this.subscribed.set(false);
			return;
		}

		const perm = await PushNotifications.checkPermissions();

		if (perm.receive === 'granted') {
			await this.subscribe();
		} else if (perm.receive !== 'denied' && !lsGet(ASKED_KEY)) {
			lsSet(ASKED_KEY, '1');
			await this.subscribe();
		}
	}

	async refresh(): Promise<void> {
		const perm = await PushNotifications.checkPermissions();
		this.subscribed.set(
			perm.receive === 'granted' && !!lsGet(TOKEN_KEY) && !lsGet(OPT_OUT_KEY),
		);
	}

	async subscribe(): Promise<boolean> {
		this.busy.set(true);

		try {
			let perm = await PushNotifications.checkPermissions();

			if (perm.receive !== 'granted') {
				perm = await PushNotifications.requestPermissions();
			}

			if (perm.receive !== 'granted') {
				return false;
			}

			const token = await new Promise<string | null>((resolve) => {
				this._pendingToken = resolve;
				void PushNotifications.register();
				setTimeout(() => {
					if (this._pendingToken === resolve) {
						this._pendingToken = null;
						resolve(null);
					}
				}, 15000);
			});

			if (!token) {
				return false;
			}

			await this.sendToken(token);
			lsSet(OPT_OUT_KEY, null);
			this.subscribed.set(true);
			return true;
		} catch (e) {
			console.warn('[FCM] subscribe failed', e);
			return false;
		} finally {
			this.busy.set(false);
		}
	}

	/** User switched phone notifications off in Delivery. */
	async unsubscribe(): Promise<void> {
		this.busy.set(true);

		try {
			await this.forgetDevice();
			lsSet(OPT_OUT_KEY, '1');
		} finally {
			this.busy.set(false);
		}
	}

	/** Logout hook — unlink this phone from the account while the session still works. */
	async forgetDevice(): Promise<void> {
		const token = lsGet(TOKEN_KEY);
		this.subscribed.set(false);

		if (!token) {
			return;
		}

		lsSet(TOKEN_KEY, null);

		try {
			await firstValueFrom(
				this._http.request('delete', `${environment.API_URL}/notifications/devices`, {
					body: { token },
				}),
			);
		} catch (e) {
			console.warn('[FCM] unregister failed', e);
		}
	}

	private async sendToken(token: string): Promise<void> {
		let appVersion: string | undefined;

		try {
			appVersion = (await App.getInfo()).version;
		} catch {
			appVersion = undefined;
		}

		await firstValueFrom(
			this._http.post(`${environment.API_URL}/notifications/devices`, {
				token,
				platform: Capacitor.getPlatform(),
				app_version: appVersion,
			}),
		);
		lsSet(TOKEN_KEY, token);
	}
}
